import {
  Box,
  Select,
  Chip,
  MenuItem,
  Typography,
  FormControl,
  Checkbox,
  Popover,
  Paper,
} from '@mui/material';
import Menu from '@mui/material/Menu';
import { styled } from '@mui/material/styles';
import React, { useCallback } from 'react';

import ClearIcon from 'common/shared-ui/src/icons/ClearIcon';

import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';

// Original class: formControl
const StyledFormControl = styled(FormControl)(theme => ({
  display: 'flex',
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'baseline',

  '& .MuiSelect-icon': {
    display: 'none',
  },

  '& .MuiInputBase-formControl': {
    width: '100%',
    height: 'auto !important',
  },
}));
// Original class: groupIcon
const StyledGroupIcon = styled(Box)(theme => ({
  '& .MuiSvgIcon-root.MuiSvgIcon-fontSizeSmall': {
    right: 10,
  },
}));
// Original class: optionsGroupMenu
const StyledOptionsGroupMenu = styled(Box)(({ theme }) => ({
  '&.MuiPopover-root': {
    zIndex: 12000,
  },

  '& .MuiPaper-root': {
    borderRadius: theme.spacing(4),
    maxHeight: '40vh',
    overflow: 'auto',
    '& ul': {
      padding: 0,
    },
  },
}));
// Original class: chosenTotals
const StyledChosenTotals = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  backgroundColor: '#ffffff',
  border: '1px solid #E4E4EF',
  borderRadius: theme.spacing(4),
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
  maxHeight: 30,
  zIndex: 1400,
  ...theme.typography.h5,

  '& .MuiChip-label': {
    padding: 0,
  },
}));
// Original class: totalsChip
const StyledTotalsChip = styled(Chip)(theme => ({
  '&.MuiChip-root': {
    height: 'unset',
    backgroundColor: 'transparent',
  },
}));
// Original class: totalsRemove
const StyledTotalsRemove = styled(ClearIcon)(({ theme }) => ({
  '&.MuiSvgIcon-root.MuiChip-deleteIcon': {
    position: 'relative !important',
    zIndex: 3000,
    padding: 0,
    marginLeft: theme.spacing(1),
    marginRight: 0,
  },
}));
// Original class: menuItemMultiply
const StyledMenuItemMultiply = styled(MenuItem)(theme => ({
  '& .MuiTypography-root': {
    textTransform: 'unset',
    color: '#212346',
  },
}));
// Original class: menuItemCheckbox
const StyledMenuItemCheckbox = styled(Checkbox)(theme => ({
  color: 'red',

  '& .MuiSvgIcon-root': {
    width: 16,
    height: 16,
    color: '#3448FF',
  },
}));
// Original class: createSelect
const StyledCreateSelect = styled(Select)(theme => ({
  '&.MuiInputBase-root.Mui-disabled': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E4E4EF',
    },
    opacity: '70%',
  },

  border: '16px',
}));
// Original class: menuItem
const StyledMenuItem = styled(MenuItem)(theme => ({
  '&.Mui-selected': {
    background: '#DCDFF4 !important',
  },

  '& .MuiTypography-root': {
    textTransform: 'unset',
    color: '#212346',
  },
}));
const ResplanSettingsSelects = ({ settings, setSettings, changed }) => {
  const getLocalString = useTranslation();
  const selectLabel = getLocalString('labels', 'select');
  const contractTracker = getLocalString(
    'AdminResourcePlan',
    'labels',
    'contractTracker',
  );
  const roleResource = getLocalString(
    'AdminResourcePlan',
    'labels',
    'roleResource',
  );
  const dismissalsDate = getLocalString(
    'AdminResourcePlan',
    'labels',
    'dismissalsDate',
  );
  const department = getLocalString(
    'AdminResourcePlan',
    'labels',
    'department',
  );
  const userAvailiability = getLocalString(
    'AdminResourcePlan',
    'labels',
    'userAvailiability',
  );
  const businessUnit = getLocalString(
    'AdminResourcePlan',
    'labels',
    'businessUnit',
  );
  const accessRole = getLocalString(
    'AdminResourcePlan',
    'labels',
    'accessRole',
  );

  const handleSelectChange = (event, settingKey, filterType) => {
    const newValueId = event.target.value;
    switch (filterType) {
      case 'value':
        setSettings(prevSettings => ({
          ...prevSettings,
          [settingKey]: prevSettings[settingKey].map(setting =>
            setting.valueId === newValueId
              ? { ...setting, isChecked: !setting.isChecked }
              : { ...setting, isChecked: false },
          ),
        }));
        break;
      case 'field':
        setSettings(prevSettings => ({
          ...prevSettings,
          [settingKey]: prevSettings[settingKey].map(setting =>
            setting.fieldId === newValueId
              ? { ...setting, isChecked: !setting.isChecked }
              : { ...setting, isChecked: false },
          ),
        }));
        break;
      case 'accessRole':
        const newValueIds = event.target.value;
        setSettings(prevSettings => ({
          ...prevSettings,
          [settingKey]: prevSettings[settingKey].map(setting => {
            const shouldBeChecked = newValueIds.includes(setting.valueId);
            return { ...setting, isChecked: shouldBeChecked };
          }),
        }));
        break;
    }
    changed();
  };
  const handleRemoveRoles = useCallback(
    e => {
      const value = e.currentTarget.attributes.value.value;
      setSettings(prevSettings => ({
        ...prevSettings,
        accessRoles: prevSettings.accessRoles.map(setting =>
          setting.valueId === value
            ? { ...setting, isChecked: false }
            : { ...setting },
        ),
      }));
      changed();
    },
    [setSettings],
  );

  return (
    <>
      <Typography
        variant='h5'
        sx={{
          marginBottom: 1,
          color: '#41424E',
        }}
      >
        {accessRole}
      </Typography>
      <StyledFormControl>
        <Select
          multiple
          defaultValue={0}
          value={
            settings.accessRoles.every(setting => !setting.isChecked)
              ? [0]
              : settings.accessRoles
                  .filter(setting => setting.isChecked)
                  .map(setting => setting.valueId)
          }
          MenuProps={{
            style: {
              maxHeight: 500,
            },
          }}
          IconComponent={() => (
            <StyledGroupIcon>
              <ChevronIcon
                direction='down'
                fontSize='small'
                viewBox='0 0 24 24'
              />
            </StyledGroupIcon>
          )}
          onChange={event =>
            handleSelectChange(event, 'accessRoles', 'accessRole')
          }
          renderValue={selected => {
            if (selected.length === 1 && selected[0] === 0) {
              return selectLabel;
            } else {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  {selected.map(value => {
                    if (value < 0) {
                      return;
                    }
                    const chipLabel = settings.accessRoles.find(
                      item => item.valueId == value,
                    ).valueName;
                    return (
                      <StyledChosenTotals
                        wrap='nowrap'
                        key={value}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <StyledTotalsChip
                          label={chipLabel}
                          value={value}
                          deleteIcon={
                            <StyledTotalsRemove
                              width={16}
                              height={16}
                              value={value}
                              viewBox='0 0 24 24'
                              onMouseDown={event => event.stopPropagation()}
                            />
                          }
                          onDelete={handleRemoveRoles}
                        />
                      </StyledChosenTotals>
                    );
                  })}
                </Box>
              );
            }
          }}
        >
          <MenuItem disabled value={0} key={0}>
            <Typography
              variant='h4'
              sx={{
                color: '#212346',
              }}
            >
              {selectLabel}
            </Typography>
          </MenuItem>
          {settings.accessRoles
            .sort((a, b) => b.isChecked - a.isChecked)
            .map(setting => (
              <StyledMenuItemMultiply
                key={setting.valueId}
                value={setting.valueId}
              >
                <StyledMenuItemCheckbox checked={setting.isChecked} />
                <Typography
                  variant='h4'
                  sx={{
                    color: '#212346',
                  }}
                >
                  {setting.valueName}
                </Typography>
              </StyledMenuItemMultiply>
            ))}
        </Select>
      </StyledFormControl>
      <Typography
        variant='h5'
        sx={{
          marginBottom: 1,
          marginTop: 5,
          color: '#41424E',
        }}
      >
        {contractTracker}
      </Typography>
      <StyledCreateSelect
        variant='outlined'
        IconComponent={() => (
          <StyledGroupIcon>
            <ChevronIcon
              direction='down'
              fontSize='small'
              viewBox='0 0 24 24'
            />
          </StyledGroupIcon>
        )}
        value={
          settings.contractTracker.find(setting => setting.isChecked)
            ?.valueId || '0'
        }
        defaultValue={0}
        onChange={event =>
          handleSelectChange(event, 'contractTracker', 'value')
        }
        MenuProps={{
          slotProps: {
            paper: {
              sx: {
                borderRadius: '16px',
                maxHeight: '40vh',
                overflow: 'auto',
                marginTop: '15px',
              },
            },
          },
        }}
      >
        <MenuItem disabled value={0} key={0}>
          <Typography
            variant='h4'
            sx={{
              color: '#212346',
            }}
          >
            {selectLabel}
          </Typography>
        </MenuItem>
        {settings.contractTracker
          .sort((a, b) => b.isChecked - a.isChecked)
          .map(setting => (
            <StyledMenuItem
              key={setting.valueId}
              value={setting.valueId}
              onClick={event =>
                handleSelectChange(event, 'contractTracker', 'value')
              }
            >
              <Typography
                variant='h4'
                sx={{
                  color: '#212346',
                }}
              >
                {setting.valueName}
              </Typography>
            </StyledMenuItem>
          ))}
      </StyledCreateSelect>
      <Typography
        variant='h5'
        sx={{
          marginTop: 5,
          marginBottom: 1,
          color: '#41424E',
        }}
      >
        {roleResource}
      </Typography>
      <StyledCreateSelect
        variant='outlined'
        IconComponent={() => (
          <StyledGroupIcon>
            <ChevronIcon
              direction='down'
              fontSize='small'
              viewBox='0 0 24 24'
            />
          </StyledGroupIcon>
        )}
        value={
          settings.roleResource.find(setting => setting.isChecked)?.fieldId ||
          '0'
        }
        defaultValue={0}
        onChange={event => handleSelectChange(event, 'roleResource', 'field')}
        MenuProps={{
          slotProps: {
            paper: {
              sx: {
                borderRadius: '16px',
                maxHeight: '40vh',
                overflow: 'auto',
                marginTop: '15px',
              },
            },
          },
        }}
      >
        <MenuItem disabled value={0} key={0}>
          <Typography
            variant='h4'
            sx={{
              color: '#212346',
            }}
          >
            {selectLabel}
          </Typography>
        </MenuItem>
        {settings.roleResource
          .sort((a, b) => b.isChecked - a.isChecked)
          .map(setting => (
            <StyledMenuItem
              key={setting.fieldId}
              value={setting.fieldId}
              onClick={event =>
                handleSelectChange(event, 'roleResource', 'field')
              }
            >
              <Typography
                variant='h4'
                sx={{
                  color: '#212346',
                }}
              >
                {setting.fieldName}
              </Typography>
            </StyledMenuItem>
          ))}
      </StyledCreateSelect>
      <Typography
        variant='h5'
        sx={{
          marginTop: 5,
          marginBottom: 1,
          color: '#41424E',
        }}
      >
        {dismissalsDate}
      </Typography>
      <StyledCreateSelect
        variant='outlined'
        IconComponent={() => (
          <StyledGroupIcon>
            <ChevronIcon
              direction='down'
              fontSize='small'
              viewBox='0 0 24 24'
            />
          </StyledGroupIcon>
        )}
        value={
          settings.dismissalsDate.find(setting => setting.isChecked)?.fieldId ||
          '0'
        }
        defaultValue={0}
        onChange={event => handleSelectChange(event, 'dismissalsDate', 'field')}
        MenuProps={{
          slotProps: {
            paper: {
              sx: {
                borderRadius: '16px',
                maxHeight: '40vh',
                overflow: 'auto',
                marginTop: '15px',
              },
            },
          },
        }}
      >
        <MenuItem disabled value={0} key={0}>
          <Typography
            variant='h4'
            sx={{
              color: '#212346',
            }}
          >
            {selectLabel}
          </Typography>
        </MenuItem>
        {settings.dismissalsDate
          .sort((a, b) => b.isChecked - a.isChecked)
          .map(setting => (
            <StyledMenuItem
              key={setting.fieldId}
              value={setting.fieldId}
              onClick={event =>
                handleSelectChange(event, 'dismissalsDate', 'field')
              }
            >
              <Typography
                variant='h4'
                sx={{
                  color: '#212346',
                }}
              >
                {setting.fieldName}
              </Typography>
            </StyledMenuItem>
          ))}
      </StyledCreateSelect>
      <Typography
        variant='h5'
        sx={{
          marginTop: 5,
          marginBottom: 1,
          color: '#41424E',
        }}
      >
        {department}
      </Typography>
      <StyledCreateSelect
        variant='outlined'
        IconComponent={() => (
          <StyledGroupIcon>
            <ChevronIcon
              direction='down'
              fontSize='small'
              viewBox='0 0 24 24'
            />
          </StyledGroupIcon>
        )}
        value={
          settings.department.find(setting => setting.isChecked)?.fieldId || '0'
        }
        defaultValue={0}
        onChange={event => handleSelectChange(event, 'department', 'field')}
        MenuProps={{
          slotProps: {
            paper: {
              sx: {
                borderRadius: '16px',
                maxHeight: '40vh',
                overflow: 'auto',
                marginTop: '15px',
              },
            },
          },
        }}
      >
        <MenuItem disabled value={0} key={0}>
          <Typography
            variant='h4'
            sx={{
              color: '#212346',
            }}
          >
            {selectLabel}
          </Typography>
        </MenuItem>
        {settings.department
          .sort((a, b) => b.isChecked - a.isChecked)
          .map(setting => (
            <StyledMenuItem
              key={setting.fieldId}
              value={setting.fieldId}
              onClick={event =>
                handleSelectChange(event, 'department', 'field')
              }
            >
              <Typography
                variant='h4'
                sx={{
                  color: '#212346',
                }}
              >
                {setting.fieldName}
              </Typography>
            </StyledMenuItem>
          ))}
      </StyledCreateSelect>
      <Typography
        variant='h5'
        sx={{
          marginTop: 5,
          marginBottom: 1,
          color: '#41424E',
        }}
      >
        {userAvailiability}
      </Typography>
      <StyledCreateSelect
        variant='outlined'
        IconComponent={() => (
          <StyledGroupIcon>
            <ChevronIcon
              direction='down'
              fontSize='small'
              viewBox='0 0 24 24'
            />
          </StyledGroupIcon>
        )}
        value={
          settings.userAvailability.find(setting => setting.isChecked)
            ?.fieldId || '0'
        }
        defaultValue={0}
        onChange={event =>
          handleSelectChange(event, 'userAvailability', 'field')
        }
        MenuProps={{
          slotProps: {
            paper: {
              sx: {
                borderRadius: '16px',
                maxHeight: '40vh',
                overflow: 'auto',
                marginTop: '15px',
              },
            },
          },
        }}
      >
        <MenuItem disabled value={0} key={0}>
          <Typography
            variant='h4'
            sx={{
              color: '#212346',
            }}
          >
            {selectLabel}
          </Typography>
        </MenuItem>
        {settings.userAvailability
          .sort((a, b) => b.isChecked - a.isChecked)
          .map(setting => (
            <StyledMenuItem
              key={setting.fieldId}
              value={setting.fieldId}
              onClick={event =>
                handleSelectChange(event, 'userAvailability', 'field')
              }
            >
              <Typography
                variant='h4'
                sx={{
                  color: '#212346',
                }}
              >
                {setting.fieldName}
              </Typography>
            </StyledMenuItem>
          ))}
      </StyledCreateSelect>
      <Typography
        variant='h5'
        sx={{
          marginTop: 5,
          marginBottom: 1,
          color: '#41424E',
        }}
      >
        {businessUnit}
      </Typography>
      <StyledCreateSelect
        variant='outlined'
        IconComponent={() => (
          <StyledGroupIcon>
            <ChevronIcon
              direction='down'
              fontSize='small'
              viewBox='0 0 24 24'
            />
          </StyledGroupIcon>
        )}
        value={
          settings.businessUnit.find(setting => setting.isChecked)?.fieldId ||
          '0'
        }
        defaultValue={0}
        onChange={event => handleSelectChange(event, 'businessUnit', 'field')}
        MenuProps={{
          slotProps: {
            paper: {
              sx: {
                borderRadius: '16px',
                maxHeight: '40vh',
                overflow: 'auto',
                marginTop: '15px',
              },
            },
          },
        }}
      >
        <MenuItem disabled value={0} key={0}>
          <Typography
            variant='h4'
            sx={{
              color: '#212346',
            }}
          >
            {selectLabel}
          </Typography>
        </MenuItem>
        {settings.businessUnit
          .sort((a, b) => b.isChecked - a.isChecked)
          .map(setting => (
            <StyledMenuItem
              key={setting.fieldId}
              value={setting.fieldId}
              onClick={event =>
                handleSelectChange(event, 'businessUnit', 'field')
              }
            >
              <Typography
                variant='h4'
                sx={{
                  color: '#212346',
                }}
              >
                {setting.fieldName}
              </Typography>
            </StyledMenuItem>
          ))}
      </StyledCreateSelect>
    </>
  );
};

export default ResplanSettingsSelects;
